import React, { useMemo, useState } from 'react';
import { useRecoilState } from 'recoil';
import { confirmationModalAtom } from 'services/modals/atoms';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  SxProps,
  Box,
  Typography
} from '@mui/material';
import { Button } from 'system/atoms';
import { ConfirmationModalType } from 'services/modals/constants';
import { Checkbox } from 'common/form';

const container: SxProps = {
  p: 1
};

const contentContainer: SxProps = {
  maxWidth: 500
};

const buttonStyle: SxProps = {
  whiteSpace: 'nowrap',
  px: 5
};

const ConfirmationModal = () => {
  const [modal, setModal] = useRecoilState(confirmationModalAtom);
  const modalType = modal.modalType;
  const width = modal.width;

  const [dontShowAgain, setDontShowAgain] = useState(false);

  const hideModal = () => {
    setDontShowAgain(false);
    setModal({
      isOpen: false,
      message: '',
      onConfirm: () => undefined,
      title: '',
      confirmationText: undefined,
      secondaryText: undefined,
      showSecondaryButton: false,
      onSecondaryClick: undefined,
      modalType: ConfirmationModalType.DEFAULT,
      hideCancel: false
    });
  };

  const handleOnClick = (
    handler: ((dontShowAgainCheck?: boolean) => void | undefined) | undefined
  ) => {
    if (handler) {
      handler(dontShowAgain);
    }
    hideModal();
  };

  const buttonProps = useMemo(() => {
    if (modalType === ConfirmationModalType.DESTRUCTIVE) {
      return {
        destructive: {
          variant: 'tertiary' as const,
          state: 'destructive' as const
        },
        cancel: {
          variant: 'primary' as const
        }
      };
    }

    return {
      cancel: {
        variant: 'tertiary' as const
      },
      confirm: {
        variant: 'primary' as const
      }
    };
  }, [modalType]);

  return (
    <Dialog open={modal.isOpen} onClose={hideModal} sx={container}>
      <DialogTitle fontWeight="bold">{modal.title}</DialogTitle>
      <DialogContent
        sx={{
          ...contentContainer,
          width: width || 'auto'
        }}
      >
        {modal.message && typeof modal.message === 'string' ? (
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '24px',
              color: 'customGrey.dark'
            }}
          >
            {modal.message}
          </Typography>
        ) : (
          <Box>{modal.message}</Box>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 3,
            width: '100%',
            gap: 2
          }}
        >
          {modalType !== ConfirmationModalType.DESTRUCTIVE && (
            <Button
              onClick={() => handleOnClick(modal.onConfirm)}
              fullWidth
              {...buttonProps.confirm}
              sx={buttonStyle}
            >
              {modal.confirmationText || 'Confirm'}
            </Button>
          )}

          {modal.showSecondaryButton && (
            <Button
              onClick={() => handleOnClick(modal.onSecondaryClick)}
              fullWidth
              variant="secondary"
              sx={buttonStyle}
            >
              {modal.secondaryText || 'Secondary'}
            </Button>
          )}

          {!modal.hideCancel && (
            <Button
              onClick={() => handleOnClick(modal.onClose)}
              fullWidth
              {...buttonProps.cancel}
              sx={buttonStyle}
            >
              {modal?.cancelText || 'Cancel'}
            </Button>
          )}
          {modalType === ConfirmationModalType.DESTRUCTIVE && (
            <Button
              onClick={() => handleOnClick(modal.onDestructiveConfirm)}
              fullWidth
              {...buttonProps.destructive}
              sx={buttonStyle}
            >
              {modal?.destructiveText || 'Confirm'}
            </Button>
          )}
        </Box>
        {modal.showDontShowAgainCheckbox && (
          <Box sx={{ mt: 1 }}>
            <Checkbox
              field={{
                checkboxLabel: 'Don’t show this again'
              }}
              value={dontShowAgain}
              onChange={() => setDontShowAgain(!dontShowAgain)}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export { ConfirmationModal };
